import { InfoPopover } from "@/components/cinch/InfoPopover";
import { KpiTile, tonePct } from "@/components/cinch/KpiTile";

import type { ForwardResponse } from "../../api";
import { fmtCur, fmtPct, monthLabel } from "../../format";

/** Projected NRR/GDR at the +12mo horizon plus exposure & coverage. */
export function TtmForward({ data }: { data: ForwardResponse }) {
  const nrrTone = tonePct(data.projected_ttm_nrr_pct, 100, 95);
  const gdrTone = tonePct(data.projected_ttm_gdr_pct, 95, 90);
  const covTone = tonePct(data.coverage_pct, 70, 40);
  const horizonLabel = data.horizon_month
    ? monthLabel(data.horizon_month + "-01")
    : "+12 mo";

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">TTM Forward Projection</h2>
        <InfoPopover title="TTM Forward Projection — How it's calculated">
          <p>For each future month M, re-run the M-12 anchored retention calc with <i>projected</i> ending MRR per customer:</p>
          <ul className="list-disc pl-5">
            <li><b>Churned</b> already, or merged → 0</li>
            <li>CSM pick = <b>Churn</b> with anniversary ≤ M → 0</li>
            <li>Otherwise (Renew / At Risk / Unknown / no anniversary in window) → current MRR (flat)</li>
          </ul>
          <p><b>Projected GDR</b> = Σ min(end, start_M-12) / Σ start_M-12.</p>
          <p><b>Projected NRR</b> = (Σ end + committed_expansion_through_M) / Σ start_M-12. Expansion = signed Upsell Items + net-positive Replacements with effective_from ≤ M.</p>
        </InfoPopover>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        Where TTM lands by {horizonLabel} if current CSM picks hold
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label={`Proj TTM GDR · ${horizonLabel}`}
          value={fmtPct(data.projected_ttm_gdr_pct)}
          tone={gdrTone}
          sub="no expansion credit"
        />
        <KpiTile
          label={`Proj TTM NRR · ${horizonLabel}`}
          value={fmtPct(data.projected_ttm_nrr_pct)}
          tone={nrrTone}
          sub={`+${fmtCur(data.committed_expansion_12mo_arr)} signed expansion`}
        />
        <KpiTile
          label="ARR exposure · 12 mo"
          value={fmtCur(data.arr_exposure_12mo)}
          sub={`${data.upcoming_count_12mo} renewals upcoming`}
        />
        <KpiTile
          label="Coverage · 12 mo"
          value={fmtPct(data.coverage_pct)}
          tone={covTone}
          sub="of upcoming renewals called"
        />
      </div>
    </section>
  );
}
